"use client"

import { useState, useEffect } from "react"
import { useSelector } from "react-redux"
import { Users, Shield, X, Edit2, Save } from "lucide-react"

const UserManagement = () => {
  const [users, setUsers] = useState([])
  const [permissions, setPermissions] = useState({})
  const [selectedUser, setSelectedUser] = useState(null)
  const [userPermissions, setUserPermissions] = useState([])
  const [availablePermissions, setAvailablePermissions] = useState([])
  const [loading, setLoading] = useState(false)
  const [editingUser, setEditingUser] = useState(null)
  const [showPermissionModal, setShowPermissionModal] = useState(false)

  const { token } = useSelector((state) => state.auth)

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3000/api"

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${API_URL}/user-management`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      // debug: surface non-OK status codes
      if (!response.ok) {
        console.log("[v0] fetchUsers non-ok:", response.status, response.statusText)
      }
      const data = await response.json()

      if (data.success) {
        setUsers(data.data)
      } else {
        console.log("[v0] fetchUsers failed:", data)
        setUsers([])
      }
    } catch (error) {
      console.error("[v0] Error fetching users:", error)
      setUsers([])
    } finally {
      setLoading(false)
    }
  }

  // Fetch available permissions
  const fetchPermissions = async () => {
    try {
      const response = await fetch(`${API_URL}/user-management/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        console.log("[v0] fetchPermissions non-ok:", response.status, response.statusText)
      }
      const data = await response.json()

      if (data.success) {
        setPermissions(data.data.grouped_permissions)
        setAvailablePermissions(data.data.permissions)
      }
    } catch (error) {
      console.error("[v0] Error fetching permissions:", error)
    }
  }

  // Fetch user permissions
  const fetchUserPermissions = async (userId, userType) => {
    try {
      const response = await fetch(`${API_URL}/user-management/${userType}/${userId}/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (!response.ok) {
        console.log("[v0] fetchUserPermissions non-ok:", response.status, response.statusText)
      }
      const data = await response.json()

      if (data.success) {
        setUserPermissions(data.data.user_permissions)
        setSelectedUser({ id: userId, type: userType })
        setShowPermissionModal(true)
      }
    } catch (error) {
      console.error("[v0] Error fetching user permissions:", error)
    }
  }

  // Update user permissions
  const updateUserPermissions = async (userId, userType, permissionIds) => {
    try {
      const response = await fetch(`${API_URL}/user-management/${userType}/${userId}/permissions`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ permission_ids: permissionIds }),
      })
      if (!response.ok) {
        console.log("[v0] updateUserPermissions non-ok:", response.status, response.statusText)
      }
      const data = await response.json()

      if (data.success) {
        alert("Permissions updated successfully!")
        setShowPermissionModal(false)
        fetchUsers() // Refresh users list
      } else {
        alert("Failed to update permissions: " + data.message)
      }
    } catch (error) {
      console.error("[v0] Error updating permissions:", error)
      alert("Error updating permissions")
    }
  }

  // Update user role
  const updateUserRole = async (userId, userType, role, departmentId) => {
    try {
      const response = await fetch(`${API_URL}/user-management/${userType}/${userId}/role`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ role, department_id: departmentId }),
      })
      if (!response.ok) {
        console.log("[v0] updateUserRole non-ok:", response.status, response.statusText)
      }
      const data = await response.json()

      if (data.success) {
        alert("User role updated successfully!")
        setEditingUser(null)
        fetchUsers() // Refresh users list
      } else {
        alert("Failed to update role: " + data.message)
      }
    } catch (error) {
      console.error("[v0] Error updating role:", error)
      alert("Error updating role")
    }
  }

  useEffect(() => {
    fetchUsers()
    fetchPermissions()
  }, [])

  const PermissionModal = () => {
    const [selectedPermissions, setSelectedPermissions] = useState(userPermissions.map((p) => p.permission_id))

    const handlePermissionToggle = (permissionId) => {
      setSelectedPermissions((prev) =>
        prev.includes(permissionId) ? prev.filter((id) => id !== permissionId) : [...prev, permissionId],
      )
    }

    const handleSave = () => {
      updateUserPermissions(selectedUser.id, selectedUser.type, selectedPermissions)
    }

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[80vh] overflow-y-auto">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">Manage User Permissions</h3>
            <button onClick={() => setShowPermissionModal(false)} className="text-gray-400 hover:text-gray-600">
              <X className="w-6 h-6" />
            </button>
          </div>

          <div className="space-y-6">
            {Object.entries(permissions).map(([category, categoryPermissions]) => (
              <div key={category} className="border rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">{category}</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {categoryPermissions.map((permission) => (
                    <label key={permission.permission_id} className="flex items-start gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedPermissions.includes(permission.permission_id)}
                        onChange={() => handlePermissionToggle(permission.permission_id)}
                        className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                      />
                      <div>
                        <div className="font-medium text-gray-900">{permission.name}</div>
                        <div className="text-sm text-gray-500">{permission.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={() => setShowPermissionModal(false)}
              className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Save className="w-4 h-4" />
              Save Permissions
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">User Management</h2>
          <p className="text-gray-600">Manage user roles and permissions</p>
        </div>
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Shield className="w-4 h-4" />
          Super Admin Only
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">System Users</h3>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Institute
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Permissions
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {users.map((user) => (
                <tr key={`${user.user_type}-${user.id}`} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-blue-600" />
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{user.name}</div>
                        <div className="text-sm text-gray-500">{user.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === "super_admin"
                          ? "bg-purple-100 text-purple-800"
                          : user.role === "admin"
                            ? "bg-blue-100 text-blue-800"
                            : "bg-green-100 text-green-800"
                      }`}
                    >
                      {user.role.replace("_", " ").toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {user.department_name || "Not Assigned"}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {user.permissions?.length || 0} permissions
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => fetchUserPermissions(user.id, user.user_type)}
                      className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                    >
                      <Shield className="w-4 h-4" />
                      Permissions
                    </button>
                    <button
                      onClick={() => setEditingUser(user)}
                      className="text-green-600 hover:text-green-900 flex items-center gap-1"
                    >
                      <Edit2 className="w-4 h-4" />
                      Edit Role
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Permission Modal */}
      {showPermissionModal && <PermissionModal />}

      {/* Edit Role Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Edit User Role</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">User: {editingUser.name}</label>
              </div>
              {editingUser.user_type === "admin" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                    <option value="account">Account</option>
                  </select>
                </div>
              )}
              {editingUser.user_type !== "admin" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <select
                    value={editingUser.role}
                    onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
              )}
              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setEditingUser(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-lg hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    updateUserRole(editingUser.id, editingUser.user_type, editingUser.role, editingUser.department_id)
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default UserManagement
